import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  TAX_ACCOUNT_DATE_NOTE,
  TAX_ACCOUNT_EXPLANATIONS,
  TAX_ACCOUNT_STATUS_LABELS,
  can,
  formatSctPeriod,
  hasBalanceInFavour,
  type TaxAccountAmounts,
  type TaxAccountStatementSummary,
  type TaxAccountStatementView,
  type TaxAccountStatus,
} from "@devengo/shared";

import { FilePicker } from "../components/file-picker";
import { Dialog } from "../components/period-picker";
import {
  Badge,
  Button,
  Card,
  ErrorBox,
  Field,
  Input,
  Notice,
  PageTitle,
  numClass,
  tableClass,
  tdClass,
  thClass,
} from "../components/ui";
import { api } from "../lib/api";
import { formatDate, formatDateTime, formatDecimal } from "../lib/format";
import { useCurrentUser } from "../lib/session";

/**
 * Cuentas tributarias (plan 017): the company's queries of ARCA's Sistema de Cuentas Tributarias.
 * Status colours are the generic report palette (as Notice and Badge), not the studio's brand.
 */
const STATUS_STYLES: Record<TaxAccountStatus, { box: string; text: string; badge: BadgeTone }> = {
  deuda: { box: "border-red-300 bg-red-50", text: "text-red-800", badge: "danger" },
  a_revisar: { box: "border-amber-300 bg-amber-50", text: "text-amber-900", badge: "warning" },
  sin_deuda: {
    box: "border-emerald-300 bg-emerald-50",
    text: "text-emerald-800",
    badge: "success",
  },
  sin_informacion: {
    box: "border-brand-line bg-brand-tint",
    text: "text-brand-text",
    badge: "neutral",
  },
};
type BadgeTone = "neutral" | "warning" | "danger" | "success";

/** «$ 1.234,56», and «-$ 1.234,56» for a balance in favour. */
const pesos = (value: string) => {
  const text = formatDecimal(value);
  return text.startsWith("-") ? `-$ ${text.slice(1)}` : `$ ${text}`;
};
const AMOUNT_HEADERS = [
  "Capital pendiente",
  "Int. resarcitorios",
  "Int. punitorios",
  "Total a pagar",
];
const IN_FAVOUR_HEADER = "Saldos a favor (a revisar)";
/** Balances in favour get their own column, never subtracted from the total (Codex 017 M2). */
const amountList = (a: TaxAccountAmounts, inFavour: boolean) => [
  a.capital,
  a.compensatoryInterest,
  a.punitiveInterest,
  a.total,
  ...(inFavour ? [a.inFavour] : []),
];
const amountHeaders = (inFavour: boolean) => [
  ...AMOUNT_HEADERS,
  ...(inFavour ? [IN_FAVOUR_HEADER] : []),
];

type Tab = "resumen" | "detalle" | "historial";

export function TaxAccountsPage({ companyId }: { companyId: string }) {
  const user = useCurrentUser();
  const reviewer = can(user.role, "manageTaxAccounts");
  const [tab, setTab] = useState<Tab>("resumen");
  const [chosen, setChosen] = useState<string | null>(null);
  const [dialog, setDialog] = useState<"import" | "no-debt" | null>(null);
  const [deleting, setDeleting] = useState<TaxAccountStatementSummary | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["tax-accounts", companyId],
    queryFn: () =>
      api.get<TaxAccountStatementSummary[]>(`/api/companies/${companyId}/tax-accounts`),
  });
  const current = list.data?.find((s) => s.current);
  const shownId =
    (chosen && list.data?.some((s) => s.id === chosen) ? chosen : null) ?? current?.id;
  const detail = useQuery({
    queryKey: ["tax-account", shownId],
    queryFn: () => api.get<TaxAccountStatementView>(`/api/tax-accounts/${shownId}`),
    enabled: shownId !== undefined,
  });
  const shown = detail.data;

  // After saving, the screen shows the latest query: an older one goes to the history with a
  // notice, so a «sin deuda» of last month never sits on top of a newer debt.
  const onSaved = (result: { statement: TaxAccountStatementView; duplicate: boolean }) => {
    const { statement, duplicate } = result;
    setDialog(null);
    setChosen(null);
    setTab("resumen");
    const when = `intereses al ${formatDate(statement.interestsOn)}`;
    if (duplicate) setNotice(`Esta consulta ya estaba cargada (${when}): no se duplicó.`);
    else if (!statement.current) {
      setNotice(
        `Se guardó la consulta (${when}) en el Historial. Arriba sigue la última, que es más reciente.`,
      );
    } else setNotice(null);
  };

  return (
    <>
      <PageTitle
        subtitle={
          current
            ? `Última consulta · intereses al ${formatDate(current.interestsOn)}`
            : "Todavía no hay consultas cargadas para esta empresa."
        }
        actions={
          <>
            <Button onClick={() => setDialog("import")}>Importar Excel ARCA</Button>
            {reviewer ? (
              <Button variant="secondary" onClick={() => setDialog("no-debt")}>
                Registrar consulta sin deuda
              </Button>
            ) : null}
            {shown ? (
              <>
                <a
                  className={linkClass}
                  href={`/api/tax-accounts/${shown.id}/resumen.xlsx`}
                  download
                >
                  Descargar Excel
                </a>
                <a
                  className={linkClass}
                  href={`/api/tax-accounts/${shown.id}/resumen.pdf`}
                  download
                >
                  Descargar PDF
                </a>
              </>
            ) : null}
          </>
        }
      >
        Cuentas tributarias
      </PageTitle>

      <div className="flex flex-col gap-4">
        {notice ? <Notice tone="info">{notice}</Notice> : null}
        <ErrorBox error={list.error ?? detail.error} />
        {list.isLoading ? <p className="text-sm text-brand-muted">Cargando…</p> : null}
        {list.data && !current ? <NoInformation /> : null}
        {shown ? (
          <>
            {!shown.current ? (
              <Notice tone="warning">
                Estás viendo una consulta anterior (intereses al {formatDate(shown.interestsOn)}).{" "}
                <button
                  type="button"
                  className="font-medium underline"
                  onClick={() => setChosen(null)}
                >
                  Volver a la última
                </button>
              </Notice>
            ) : null}
            <StatusBanner statement={shown} />
            <Tabs value={tab} onChange={setTab} />
            {tab === "resumen" ? <Summary statement={shown} /> : null}
            {tab === "detalle" ? <Detail statement={shown} /> : null}
          </>
        ) : null}
        {tab === "historial" && list.data && list.data.length > 0 ? (
          <History
            statements={list.data}
            shownId={shownId}
            reviewer={reviewer}
            onShow={(id) => {
              setChosen(id);
              setTab("resumen");
            }}
            onDelete={setDeleting}
          />
        ) : null}
      </div>

      {dialog === "import" ? (
        <ImportDialog
          companyId={companyId}
          reviewer={reviewer}
          onClose={() => setDialog(null)}
          onSaved={onSaved}
        />
      ) : null}
      {dialog === "no-debt" ? (
        <NoDebtDialog companyId={companyId} onClose={() => setDialog(null)} onSaved={onSaved} />
      ) : null}
      {deleting ? (
        <DeleteDialog
          companyId={companyId}
          statement={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            setChosen(null);
          }}
        />
      ) : null}
    </>
  );
}

const linkClass =
  "inline-flex items-center rounded-md border border-brand-line bg-white px-3 py-1.5 text-sm font-medium hover:bg-brand-tint";

function NoInformation() {
  const style = STATUS_STYLES.sin_informacion;
  return (
    <div className={`rounded-lg border px-4 py-4 ${style.box}`}>
      <p className={`font-semibold ${style.text}`}>{TAX_ACCOUNT_STATUS_LABELS.sin_informacion}</p>
      <p className="mt-1 text-sm text-brand-muted">
        Todavía no se cargó ninguna consulta para esta empresa.{" "}
        <strong>Sin información no equivale a sin deuda.</strong> Bajá el Excel desde «Sistema de
        Cuentas Tributarias» en ARCA e importalo, o registrá una consulta sin deuda si ARCA no
        informa nada.
      </p>
    </div>
  );
}

function StatusBanner({ statement }: { statement: TaxAccountStatementView }) {
  const style = STATUS_STYLES[statement.status];
  const explain =
    statement.source === "manual"
      ? `Consulta registrada a mano: ARCA no informó deuda al ${formatDate(statement.interestsOn)}.`
      : `Capital e intereses calculados al ${formatDate(statement.interestsOn)} · emitida el ${formatDate(statement.issuedOn)}`;
  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border-l-4 px-4 py-3 ${style.box}`}
      role="status"
    >
      <div>
        <p className={`text-sm font-bold uppercase ${style.text}`}>
          {TAX_ACCOUNT_STATUS_LABELS[statement.status]}
        </p>
        <p className={`text-xs ${style.text}`}>{explain}</p>
        {statement.status === "a_revisar" ? (
          <p className={`mt-1 text-xs ${style.text}`}>
            Hay importes negativos (saldos a favor): no se compensan con la deuda. Revisalos en el
            detalle.
          </p>
        ) : null}
      </div>
      <div className={`text-right ${style.text}`}>
        <p className="text-2xl font-bold tabular">{pesos(statement.totals.total)}</p>
        {hasBalanceInFavour(statement.totals) ? (
          <p className="text-xs tabular">
            a pagar · saldos a favor a revisar {pesos(statement.totals.inFavour)}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function Tabs({ value, onChange }: { value: Tab; onChange: (tab: Tab) => void }) {
  const tabs: { id: Tab; label: string }[] = [
    { id: "resumen", label: "Resumen" },
    { id: "detalle", label: "Detalle" },
    { id: "historial", label: "Historial" },
  ];
  return (
    <div role="tablist" className="flex gap-1 border-b border-brand-line">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={value === t.id}
          className={`-mb-px border-b-2 px-3 py-2 text-sm ${
            value === t.id
              ? "border-brand-primary font-semibold text-brand-primary"
              : "border-transparent text-brand-muted hover:text-brand-text"
          }`}
          onClick={() => onChange(t.id)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function Summary({ statement }: { statement: TaxAccountStatementView }) {
  const inFavour = hasBalanceInFavour(statement.totals);
  return (
    <>
      {statement.source === "manual" ? (
        <Card>
          <p className="text-sm">
            La consulta en ARCA al {formatDate(statement.interestsOn)} no informó capital ni
            intereses pendientes.
          </p>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className={tableClass}>
              <thead>
                <tr>
                  <th className={thClass}>Impuesto</th>
                  {amountHeaders(inFavour).map((h) => (
                    <th key={h} className={`${thClass} text-right`}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {statement.byTax.map((row) => (
                  <tr key={row.tax}>
                    <td className={tdClass}>{row.tax}</td>
                    {amountList(row, inFavour).map((v, i) => (
                      <td key={i} className={`${tdClass} ${numClass}`}>
                        {pesos(v)}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr className={`font-semibold ${STATUS_STYLES[statement.status].text}`}>
                  <td className={tdClass}>TOTAL A PAGAR</td>
                  {amountList(statement.totals, inFavour).map((v, i) => (
                    <td key={i} className={`${tdClass} ${numClass}`}>
                      {pesos(v)}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}
      <Explanations />
      <p className="text-xs text-brand-muted">
        {statement.obligationsCount}{" "}
        {statement.obligationsCount === 1 ? "obligación" : "obligaciones"} · El PDF y el Excel para
        el cliente traen solo este resumen.
      </p>
    </>
  );
}

function Explanations() {
  return (
    <section className="overflow-hidden rounded-lg border border-amber-200">
      <h2 className="bg-brand-primary px-4 py-2 text-sm font-bold uppercase text-white">
        Cómo leer los importes
      </h2>
      <dl className="divide-y divide-amber-200 bg-amber-50">
        {TAX_ACCOUNT_EXPLANATIONS.map((e) => (
          <div key={e.title} className="grid grid-cols-1 gap-1 px-4 py-2 md:grid-cols-[13rem_1fr]">
            <dt className="text-sm font-semibold text-brand-primary">{e.title}</dt>
            <dd className="text-sm">{e.body}</dd>
          </div>
        ))}
      </dl>
      <p className="bg-amber-50 px-4 pb-3 text-xs text-brand-muted">{TAX_ACCOUNT_DATE_NOTE}</p>
    </section>
  );
}

function Detail({ statement }: { statement: TaxAccountStatementView }) {
  if (statement.obligations.length === 0) {
    return (
      <Card>
        <p className="text-sm text-brand-muted">Esta consulta no tiene obligaciones.</p>
      </Card>
    );
  }
  const extras = [...new Set(statement.obligations.flatMap((o) => Object.keys(o.extraFields)))];
  const inFavour = hasBalanceInFavour(statement.totals);
  // Mathias hid Establecimiento (always 0 in practice); it shows when it tells rows apart.
  const establishments = new Set(statement.obligations.map((o) => o.establishment)).size > 1;
  const textHeaders = [
    ...(establishments ? ["Establecimiento"] : []),
    "Impuesto",
    "Concepto",
    "Subconcepto",
    "Período",
    "Cuota",
    "Vencimiento",
  ];
  return (
    <Card>
      <div className="max-h-[32rem] overflow-auto">
        <table className={tableClass}>
          <thead className="sticky top-0 bg-white">
            <tr>
              {textHeaders.map((h) => (
                <th key={h} className={thClass}>
                  {h}
                </th>
              ))}
              {amountHeaders(inFavour).map((h) => (
                <th key={h} className={`${thClass} text-right`}>
                  {h}
                </th>
              ))}
              {extras.map((h) => (
                <th key={h} className={thClass}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {statement.obligations.map((o) => (
              <tr key={o.rowNumber}>
                {establishments ? <td className={tdClass}>{o.establishment}</td> : null}
                <td className={tdClass}>{o.tax}</td>
                <td className={tdClass}>{o.concept}</td>
                <td className={tdClass}>{o.subconcept}</td>
                <td className={`${tdClass} whitespace-nowrap`}>{formatSctPeriod(o.period)}</td>
                <td className={tdClass}>{o.installment}</td>
                <td className={`${tdClass} whitespace-nowrap`}>{formatDate(o.dueDate)}</td>
                {amountList(o, inFavour).map((v, i) => (
                  <td
                    key={i}
                    className={`${tdClass} ${numClass} ${v.startsWith("-") ? "text-amber-800" : ""}`}
                  >
                    {pesos(v)}
                  </td>
                ))}
                {extras.map((h) => (
                  <td key={h} className={tdClass}>
                    {String(o.extraFields[h] ?? "")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {statement.hasFile ? (
        <p className="mt-3 text-xs text-brand-muted">
          <a className="underline" href={`/api/tax-accounts/${statement.id}/original`}>
            Bajar el archivo original de ARCA
          </a>
        </p>
      ) : null}
    </Card>
  );
}

function History({
  statements,
  shownId,
  reviewer,
  onShow,
  onDelete,
}: {
  statements: TaxAccountStatementSummary[];
  shownId: string | undefined;
  reviewer: boolean;
  onShow: (id: string) => void;
  onDelete: (statement: TaxAccountStatementSummary) => void;
}) {
  return (
    <Card>
      <p className="mb-2 text-sm text-brand-muted">
        Cada consulta se guarda aparte y no se suma con otras. La que se muestra arriba es la de
        mayor fecha de intereses.
      </p>
      <div className="overflow-x-auto">
        <table className={tableClass}>
          <thead>
            <tr>
              <th className={thClass}>Intereses al</th>
              <th className={thClass}>Emitida</th>
              <th className={thClass}>Origen</th>
              <th className={thClass}>Estado</th>
              <th className={`${thClass} text-right`}>Total a pagar</th>
              <th className={thClass}>Cargada</th>
              <th className={thClass} />
            </tr>
          </thead>
          <tbody>
            {statements.map((s) => (
              <tr key={s.id} className={s.id === shownId ? "bg-brand-tint" : ""}>
                <td className={tdClass}>
                  {formatDate(s.interestsOn)}{" "}
                  {s.current ? <Badge tone="brand">Última</Badge> : null}
                </td>
                <td className={tdClass}>{formatDate(s.issuedOn)}</td>
                <td className={tdClass}>
                  {s.source === "manual" ? "Registrada sin deuda" : "Excel de ARCA"}
                </td>
                <td className={tdClass}>
                  <Badge tone={STATUS_STYLES[s.status].badge}>
                    {TAX_ACCOUNT_STATUS_LABELS[s.status]}
                  </Badge>
                </td>
                <td className={`${tdClass} ${numClass}`}>{pesos(s.totals.total)}</td>
                <td className={`${tdClass} whitespace-nowrap`}>{formatDateTime(s.createdAt)}</td>
                <td className={`${tdClass} whitespace-nowrap text-right`}>
                  <Button variant="ghost" onClick={() => onShow(s.id)}>
                    Ver
                  </Button>
                  {reviewer ? (
                    <Button variant="ghost" onClick={() => onDelete(s)}>
                      Eliminar
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

type Saved = { statement: TaxAccountStatementView; duplicate: boolean };

function useRefresh(companyId: string) {
  const client = useQueryClient();
  return async () => {
    await client.invalidateQueries({ queryKey: ["tax-accounts", companyId] });
    await client.invalidateQueries({ queryKey: ["tax-account"] });
  };
}

/** What the import answers for ARCA's Vencimientos export: nothing stored, its due dates. */
type DueDatesAnswer = {
  dueDates: {
    issuedOn: string;
    rows: {
      tax: string;
      concept: string;
      period: string;
      installment: string;
      dueDate: string;
      detail: string;
    }[];
  };
};

function ImportDialog({
  companyId,
  reviewer,
  onClose,
  onSaved,
}: {
  companyId: string;
  reviewer: boolean;
  onClose: () => void;
  onSaved: (result: Saved) => void;
}) {
  const refresh = useRefresh(companyId);
  const [file, setFile] = useState<File | null>(null);
  const [dueDates, setDueDates] = useState<DueDatesAnswer["dueDates"] | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const upload = useMutation({
    mutationFn: () => {
      const form = new FormData();
      if (file) form.append("file", file);
      return api.upload<Saved | DueDatesAnswer>(`/api/companies/${companyId}/tax-accounts`, form);
    },
    onSuccess: async (result) => {
      if ("dueDates" in result) {
        setDueDates(result.dueDates);
        return;
      }
      await refresh();
      onSaved(result);
    },
  });
  // Maxi, 2026-10-21: with no debt, the studio downloads the Vencimientos export instead.
  const record = useMutation({
    mutationFn: () => {
      const form = new FormData();
      if (file) form.append("file", file);
      form.append("confirmed", String(confirmed));
      return api.upload<Saved>(`/api/companies/${companyId}/tax-accounts/no-debt/file`, form);
    },
    onSuccess: async (result) => {
      await refresh();
      onSaved(result);
    },
  });
  if (dueDates) {
    return (
      <Dialog title="Archivo de Vencimientos" onClose={onClose}>
        <div className="flex flex-col gap-3">
          <p className="text-sm">
            Es el listado de <b>Vencimientos</b> de ARCA, emitido el {formatDate(dueDates.issuedOn)}
            : no trae deudas ni importes. No se importó nada.
          </p>
          {dueDates.rows.length > 0 ? (
            <div className="max-h-56 overflow-y-auto">
              <table className={tableClass}>
                <thead>
                  <tr>
                    <th className={thClass}>Impuesto</th>
                    <th className={thClass}>Período</th>
                    <th className={thClass}>Vence</th>
                    <th className={thClass}>Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {dueDates.rows.map((r, i) => (
                    <tr key={i}>
                      <td className={tdClass}>{r.tax}</td>
                      <td className={`${tdClass} tabular`}>{r.period}</td>
                      <td className={`${tdClass} tabular`}>{formatDate(r.dueDate)}</td>
                      <td className={tdClass}>{r.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {reviewer ? (
            <>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />
                <span>
                  Verifiqué en ARCA que la consulta no informa capital ni intereses pendientes. Este
                  archivo queda guardado como respaldo. (Esto no verifica declaraciones juradas sin
                  presentar.)
                </span>
              </label>
              <ErrorBox error={record.error} />
            </>
          ) : (
            <p className="text-sm text-brand-muted">
              Para registrar la consulta sin deuda con este archivo, pedíselo al Contador o al
              Dueño.
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              {reviewer ? "Cancelar" : "Cerrar"}
            </Button>
            {reviewer ? (
              <Button disabled={!confirmed || record.isPending} onClick={() => record.mutate()}>
                Registrar sin deuda al {formatDate(dueDates.issuedOn)}
              </Button>
            ) : null}
          </div>
        </div>
      </Dialog>
    );
  }
  return (
    <Dialog title="Importar Excel de Cuentas Tributarias" onClose={onClose}>
      <div className="flex flex-col gap-3">
        <p className="text-sm text-brand-muted">
          En ARCA, «Sistema de Cuentas Tributarias» → consulta de deuda → exportar a Excel. Subí el
          archivo tal como se descargó (por ejemplo <code>export.xls</code>). Tiene que ser del CUIT
          de esta empresa. Si no hay deuda, también sirve el Excel de Vencimientos.
        </p>
        <FilePicker
          file={file}
          onChange={setFile}
          accept=".xls,.xlsx"
          label="Archivo de Cuentas Tributarias"
          hint="o arrastralo acá · Excel (.xls o .xlsx)"
        />
        <ErrorBox error={upload.error} />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button disabled={!file || upload.isPending} onClick={() => upload.mutate()}>
            {upload.isPending ? "Importando…" : "Importar"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

const todayAr = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "America/Argentina/Buenos_Aires" }).format(
    new Date(),
  );

function NoDebtDialog({
  companyId,
  onClose,
  onSaved,
}: {
  companyId: string;
  onClose: () => void;
  onSaved: (result: Saved) => void;
}) {
  const refresh = useRefresh(companyId);
  const [date, setDate] = useState(todayAr());
  const [confirmed, setConfirmed] = useState(false);
  const record = useMutation({
    mutationFn: () =>
      api.post<Saved>(`/api/companies/${companyId}/tax-accounts/no-debt`, {
        interestsOn: date,
        confirmed,
      }),
    onSuccess: async (result) => {
      await refresh();
      onSaved(result);
    },
  });
  return (
    <Dialog title="Registrar consulta sin deuda" onClose={onClose}>
      <div className="flex flex-col gap-3">
        <p className="text-sm text-brand-muted">
          Para cuando la consulta en ARCA no muestra deuda y no hay nada para exportar. Queda
          registrada con tu usuario.
        </p>
        <Field label="Fecha de la consulta en ARCA">
          <Input
            type="date"
            value={date}
            max={todayAr()}
            onChange={(e) => setDate(e.target.value)}
          />
        </Field>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          />
          <span>
            Verifiqué en ARCA que la consulta no informa capital ni intereses pendientes. (Esto no
            verifica declaraciones juradas sin presentar.)
          </span>
        </label>
        <ErrorBox error={record.error} />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={!confirmed || !date || record.isPending}
            onClick={() => record.mutate()}
          >
            Registrar
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function DeleteDialog({
  companyId,
  statement,
  onClose,
  onDeleted,
}: {
  companyId: string;
  statement: TaxAccountStatementSummary;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const refresh = useRefresh(companyId);
  const [reason, setReason] = useState("");
  const remove = useMutation({
    mutationFn: () => api.delete(`/api/tax-accounts/${statement.id}`, { reason }),
    onSuccess: async () => {
      await refresh();
      onDeleted();
    },
  });
  return (
    <Dialog title="Eliminar consulta" onClose={onClose}>
      <div className="flex flex-col gap-3">
        <p className="text-sm">
          Vas a eliminar la consulta con intereses al{" "}
          <strong>{formatDate(statement.interestsOn)}</strong> (
          {TAX_ACCOUNT_STATUS_LABELS[statement.status]}, {pesos(statement.totals.total)}). La
          auditoría guarda una copia y el archivo original se conserva.
        </p>
        <Field label="Motivo (obligatorio)">
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ej.: era de otra fecha"
          />
        </Field>
        <ErrorBox error={remove.error} />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="danger" disabled={remove.isPending} onClick={() => remove.mutate()}>
            Eliminar
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
